/**
 * Avrio only: matching across every customer this month, against the shared
 * monthly Nexxt unlock ceiling, with the ceiling and unlock price editable.
 * Reads GET /api/matching/usage and GET/POST /api/matching/global-limits
 * (avrio-backend#193, #194), both Super Admin only on the backend.
 *
 * The 3,000 default is Nachi's unconfirmed recollection of the Nexxt allotment
 * (2026-10-02); real usage has been far below it, so the ceiling is a safety
 * net and the page shows actual numbers rather than designing around 3,000.
 */

import { memo, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Progress,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import axios from '../../../../axiosConfig';
import { EstateMatchingUsage, GlobalMatchingLimits } from '../../types';
import { meter, monthLabel } from '../matching-usage-section/summary';
import styles from '../matching-usage-section/index.module.css';

const { Title, Text } = Typography;
const count = (n?: number | null) =>
  typeof n === 'number' ? n.toLocaleString('en-US') : '—';
const money = (n?: number | null) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(n)
    : '—';

const EstateMatchingSection = () => {
  const [usage, setUsage] = useState<EstateMatchingUsage | null>(null);
  const [limits, setLimits] = useState<GlobalMatchingLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    Promise.all([
      axios.get<EstateMatchingUsage>('/api/matching/usage'),
      axios.get<GlobalMatchingLimits>('/api/matching/global-limits'),
    ])
      .then(([u, l]) => {
        if (
          !Array.isArray(u.data?.customers) ||
          typeof l.data?.monthlyUnlockCeiling !== 'number'
        ) {
          setFailed(true);
          return;
        }
        setUsage(u.data);
        setLimits(l.data);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const save = (values: {
    monthlyUnlockCeiling: number;
    unlockPriceUsd: number;
    note?: string;
  }) => {
    setSaving(true);
    setSaveError(null);
    axios
      .post<GlobalMatchingLimits>('/api/matching/global-limits', values)
      .then((res) => {
        setLimits(res.data);
        setEditing(false);
        load();
      })
      .catch(() => setSaveError('Could not save. Try again.'))
      .finally(() => setSaving(false));
  };

  const header = (
    <Title level={5} id="estate-matching-title" className={styles.title}>
      Matching across all customers
    </Title>
  );

  if (loading) {
    return (
      <section
        className={styles.section}
        aria-labelledby="estate-matching-title"
      >
        {header}
        <div className={styles.loading}>
          <Spin size="small" />
          <Text type="secondary">Loading…</Text>
        </div>
      </section>
    );
  }
  if (failed || !usage || !limits) {
    return (
      <section
        className={styles.section}
        aria-labelledby="estate-matching-title"
      >
        {header}
        <Alert
          type="error"
          showIcon
          message="Could not load matching across customers."
          action={
            <Button size="small" onClick={load}>
              Try again
            </Button>
          }
        />
      </section>
    );
  }

  const ceiling =
    typeof usage.unlocks === 'number'
      ? meter(usage.unlocks, usage.monthlyUnlockCeiling)
      : null;

  return (
    <section className={styles.section} aria-labelledby="estate-matching-title">
      {header}
      <Text type="secondary" className={styles.intro}>
        {monthLabel(usage.month)} (UTC). Avrio only.
      </Text>

      {limits.warnings?.map((w) => (
        <Alert
          key={w}
          type="warning"
          showIcon
          message={w}
          className={styles.notice}
        />
      ))}

      {ceiling ? (
        <div className={styles.meter}>
          <div className={styles.meterHead}>
            <span className={styles.label}>Unlocks, all customers</span>
            <span className={styles.meterValue}>
              {count(ceiling.used)} of {count(ceiling.limit)} shared ceiling
            </span>
          </div>
          <Progress percent={ceiling.percent} showInfo={false} size="small" />
        </div>
      ) : (
        <Alert
          type="info"
          showIcon
          className={styles.notice}
          message="Unlock counts can't be read right now."
          description="Matching is not affected; the matching pipeline still enforces the ceiling."
        />
      )}

      <dl className={styles.figures}>
        <div>
          <dt className={styles.label}>Budgets allocated</dt>
          <dd>{count(usage.allocatedUnlocks)} unlocks</dd>
        </div>
        <div>
          <dt className={styles.label}>Paused at a customer cap</dt>
          <dd>{count(usage.pausedForJobCap)}</dd>
        </div>
        <div>
          <dt className={styles.label}>Paused at the shared ceiling</dt>
          <dd>{count(usage.pausedForCeiling)}</dd>
        </div>
        <div>
          <dt className={styles.label}>Price per unlock</dt>
          <dd>{money(limits.unlockPriceUsd)}</dd>
        </div>
      </dl>

      <Table
        size="small"
        rowKey="customerId"
        pagination={false}
        dataSource={usage.customers}
        locale={{
          emptyText: 'No customer has matched anything yet this month.',
        }}
        columns={[
          {
            title: 'Customer',
            dataIndex: 'customerName',
            render: (n: string, r) => n ?? `#${r.customerId}`,
          },
          {
            title: 'Jobs',
            render: (_: unknown, r) =>
              `${count(r.jobsMatched)} / ${count(r.monthlyJobCap)}`,
          },
          { title: 'Scored', dataIndex: 'candidatesScored', render: count },
          { title: 'Unlocks', dataIndex: 'unlocks', render: count },
          { title: 'Spend', dataIndex: 'unlockSpend', render: money },
        ]}
      />

      <div className={styles.editor}>
        {editing ? (
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              monthlyUnlockCeiling: limits.monthlyUnlockCeiling,
              unlockPriceUsd: limits.unlockPriceUsd,
            }}
            onFinish={save}
          >
            <Form.Item
              name="monthlyUnlockCeiling"
              label="Shared monthly unlock ceiling"
              rules={[
                {
                  required: true,
                  type: 'integer',
                  min: 0,
                  message: '0 or more',
                },
              ]}
            >
              <InputNumber min={0} precision={0} />
            </Form.Item>
            <Form.Item
              name="unlockPriceUsd"
              label="Price per unlock (USD)"
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: 0.01,
                  message: 'More than 0',
                },
              ]}
            >
              <InputNumber min={0.01} step={0.05} prefix="$" />
            </Form.Item>
            <Form.Item name="note" label="Note (why)">
              <Input
                maxLength={500}
                placeholder="e.g. Nexxt confirmed the allotment"
              />
            </Form.Item>
            {saveError && (
              <Alert
                type="error"
                showIcon
                message={saveError}
                className={styles.notice}
              />
            )}
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                Save
              </Button>
              <Button onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
            </Space>
          </Form>
        ) : (
          <Space direction="vertical" size={4}>
            {limits.isDefault && (
              <Text type="secondary">
                Using the default ceiling (unconfirmed with Nexxt).
              </Text>
            )}
            <Button onClick={() => setEditing(true)}>
              Edit ceiling and price
            </Button>
          </Space>
        )}
      </div>
    </section>
  );
};

export default memo(EstateMatchingSection);
